import { useCallback, useEffect, useRef, useState } from 'react';
import useProjectServices from './useProjectServices';
import { followPreviewTabs, readyServiceFor, reconcilePreviewTabs } from '../utils/previewReadiness';
import { devLog } from '../utils/devLog';

/**
 * One service state for the header and the Preview (Canvas.jsx).
 *
 * The header's "Running 1/1" (ProjectRunStatus) reads the project's services from vfs-service; the Preview tab used
 * to learn about its service only from the render call that put it in "Rendering…". When that call was cancelled
 * (its effect re-ran), skipped (the tab re-created after the load was already claimed) or failed on an answer it
 * could not read (a status poll redirected to Flow's HTML), nothing ever cleared the tab's `loading`: the Preview
 * stayed there while the header said Running, until Refresh.
 *
 * Now the Preview reads the same store as the header (useProjectServices, one answer per project for the whole page)
 * and this watchdog shows the app as soon as that state says the service is ready; after SLOW_START_MS of running
 * without being ready it reloads once, then marks the tab `slowStart` ("Taking longer than usual · Refresh · Logs").
 *
 * @param {object} options
 * @param {string|null} options.projectId the project whose services to watch; null = not watching
 * @param {object[]} options.previewTabs the Preview tabs
 * @param {Function} options.setPreviewTabs their setter
 * @param {(serviceName: string) => void} [options.onShown] a tab was shown by the watchdog (release its load claim)
 * @param {number} [options.tickMs] how often the timers are checked while a tab loads
 * It also follows the tabs that are not rendering (followPreviewTabs): whatever starts the service (the in-page Start,
 * the header's Start all, Home, Restart), the Preview reloads its iframe as soon as the state says it is ready; when it
 * stops, the tab shows the stopped state with Start instead of stale content.
 *
 * @returns {{ sharedReadyFor: (serviceName: string) => (() => object|null), canRun: boolean }}
 *   `sharedReadyFor` for pollRenderStatus's `isReady`: the shared state's ready session of a service, from an answer
 *   newer than the call.
 */
export default function usePreviewWatchdog({ projectId, previewTabs, setPreviewTabs, onShown = undefined, tickMs = 5000 }) {
  const anyLoading = previewTabs.some((tab) => tab.loading && !tab.error);
  // Stopped or starting: poll as fast as while loading, so a start from anywhere shows within a few seconds.
  const anyWaiting = anyLoading || previewTabs.some((tab) => tab.stopped || tab.starting);
  const state = useProjectServices(projectId, { intervalMs: anyWaiting ? 3000 : 10000, enabled: Boolean(projectId) });
  const known = state.known && !state.unavailable;

  const latest = useRef({ services: [], at: 0, known: false });
  latest.current = { services: state.services, at: state.at, known };
  const tabsRef = useRef(previewTabs);
  tabsRef.current = previewTabs;
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;
  const track = useRef(new Map());
  const follow = useRef(new Map());
  // When this watch began (the Preview opened, or the project changed): the tabs on screen are followed from then,
  // so the answer the watch asked for at that moment already counts (a stopped app does not show for one more poll).
  const watchStart = useRef({ projectId: null, at: 0, used: false });
  if (watchStart.current.projectId !== projectId) {
    watchStart.current = { projectId, at: Date.now(), used: false };
    follow.current.clear();
  }

  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!anyLoading) { return undefined; }
    const timer = setInterval(() => setTick((n) => n + 1), tickMs);
    return () => clearInterval(timer);
  }, [anyLoading, tickMs]);

  useEffect(() => {
    if (!state.loaded) { return; }
    const now = Date.now();
    const since = watchStart.current.used ? now : watchStart.current.at;
    watchStart.current.used = true;
    const patches = {
      ...followPreviewTabs(tabsRef.current, latest.current.services, { known: latest.current.known, answerAt: latest.current.at, now, since, follow: follow.current }),
      ...reconcilePreviewTabs(tabsRef.current, latest.current.services, { answerAt: latest.current.at, now, track: track.current }),
    };
    const names = Object.keys(patches);
    if (names.length === 0) { return; }
    names.forEach((name) => {
      if (patches[name].stopped === true) {
        devLog('⏹️ [Preview watchdog] the service state reports', name, 'stopped: showing the stopped state');
        onShownRef.current?.(name);
      } else if (patches[name].loading === false) {
        devLog('✅ [Preview watchdog] the service state reports', name, 'running: showing it');
        onShownRef.current?.(name);
      }
    });
    setPreviewTabs((current) => current.map((tab) => (patches[tab.serviceName] ? { ...tab, ...patches[tab.serviceName] } : tab)));
    // The tabs are read through a ref: a new answer, the timer tick or a tab starting to load re-check them.
  }, [state.at, state.loaded, tick, anyLoading, anyWaiting, setPreviewTabs]);

  const sharedReadyFor = useCallback((serviceName) => {
    const since = Date.now();
    return () => {
      const { services, at } = latest.current;
      return at > since ? readyServiceFor(services, serviceName) : null;
    };
  }, []);

  return { sharedReadyFor, canRun: state.canRun };
}
